import globalsCss from "@/styles/globals.css?url";
import fontsourceNotoSansSC from "@fontsource-variable/noto-sans-sc?url";
import fontsourceNotoSans from "@fontsource-variable/noto-sans?url";
import fontsourceFusionPixelSC from "@fontsource/fusion-pixel-12px-proportional-sc?url";

import { DefaultCatchBoundary } from "@/components/default-catch-boundary";
import { AppLayout } from "@/components/layout/app-layout";
import { NotFound } from "@/components/not-found";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { SessionValidationResult } from "@/libs/auth/utils";
import { setLanguageTag } from "@/libs/i18n/runtime";
import { logger } from "@/libs/logger";
import { ThemeProvider } from "@/libs/theme";
import { i18nQueryOptions, useI18nQuery } from "@/services/i18n.query";
import {
  preferenceQueryOptions,
  usePreferenceQuery,
} from "@/services/preference.query";
import { useRouteFeatures } from "@/utils/common";
import { getDeploymentInfo } from "@/utils/deployment";
import { queries } from "@/utils/query";
import { createMetadata } from "@/utils/seo";
import { Provider as ModalProvider } from "@ebay/nice-modal-react";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools/production";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { HeadContent, Scripts } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { H } from "highlight.run";
import { motion } from "motion/react";
import { outdent } from "outdent";
import type * as React from "react";
import { useEffect } from "react";

interface RouterContext {
  queryClient: QueryClient;
  auth: SessionValidationResult;
  features: string[];
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context }) => {
    const [auth, preference] = await Promise.all([
      context.queryClient.ensureQueryData(
        queries.auth.getCurrentSession.getFetchOptions(),
      ),
      context.queryClient.ensureQueryData(preferenceQueryOptions()),
    ]);

    setLanguageTag(preference.locale);
    await context.queryClient.ensureQueryData(
      i18nQueryOptions(preference.locale),
    );

    return {
      auth,
    };
  },
  head: () => ({
    meta: createMetadata({
      charSet: "utf-8",
      viewport: {
        width: "device-width",
        "initial-scale": "1",
        "maximum-scale": "1",
        "user-scalable": "no",
        "viewport-fit": "cover",
      },
      title: "Sliver Stack",
      description: "A full-stack TypeScript framework",
      robots: "follow, index",
    }),
    links: [
      {
        rel: "icon",
        href: "/favicon.ico",
      },
      {
        rel: "stylesheet",
        href: globalsCss,
      },
      {
        rel: "stylesheet",
        href: fontsourceNotoSans,
      },
      {
        rel: "stylesheet",
        href: fontsourceNotoSansSC,
      },
      {
        rel: "stylesheet",
        href: fontsourceFusionPixelSC,
      },
    ],
    scripts: import.meta.env.PROD
      ? []
      : [
          {
            src: "https://unpkg.com/react-scan/dist/auto.global.js",
          },
          {
            type: "module",
            children: outdent /* js */`
              import RefreshRuntime from "/_build/@react-refresh"
              RefreshRuntime.injectIntoGlobalHook(window)
              window.$RefreshReg$ = () => {}
              window.$RefreshSig$ = () => (type) => type
            `,
          },
        ],
  }),
  component: RootComponent,
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  pendingComponent: PendingComponent,
});

function RootDocument({ children }: React.PropsWithChildren) {
  const preferenceQuery = usePreferenceQuery();
  const i18nQuery = useI18nQuery(preferenceQuery.data.locale);

  const { data: session } = queries.auth.getCurrentSession.useQuery({
    enabled: false,
  });

  useEffect(() => {
    if (session?.user) {
      H.identify(session.user.username, {
        id: session.user.userId,
        email: session.user.email,
        username: session.user.username,
        highlightDisplayName: session.user.nickname,
        avatar: session.user.avatarUrl,
      });
    }
  }, [session]);

  useEffect(() => {
    if (!import.meta.env.SSR) {
      getDeploymentInfo().then((info) => {
        logger.info(info, "Deployment Info");
      });
    }
  }, []);

  return (
    <html
      lang={i18nQuery.data.locale}
      suppressHydrationWarning
      data-theme="moonrise"
      data-mode="dark"
    >
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider attribute="data-theme" defaultTheme="moonrise">
          <ModalProvider>
            <TooltipProvider>{children}</TooltipProvider>
            <Toaster />
          </ModalProvider>
        </ThemeProvider>
        <ReactQueryDevtools buttonPosition="bottom-left" position="left" />
        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const routeFeatures = useRouteFeatures();

  return (
    <RootDocument>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {routeFeatures.has("no-layout") ? (
          <Outlet />
        ) : (
          <AppLayout fixedHeader={routeFeatures.has("fixed-header")}>
            <Outlet />
          </AppLayout>
        )}
      </motion.div>
    </RootDocument>
  );
}

function PendingComponent() {
  return (
    <RootDocument>
      <div className="flex items-center justify-center h-full">
        <p>Loading...</p>
      </div>
    </RootDocument>
  );
}
